import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import clsx from 'clsx'
import { type ReactElement, useCallback } from 'react'
import { z } from 'zod'
import { ExportListButton } from '~/components/Filters/ExportListButton'
import { FilterBar } from '~/components/Filters/FilterBar/FilterBar'
import type { FiltersState } from '~/components/Filters/FilterBar/types'
import {
  resolveFilters,
  toFiltersState
} from '~/components/Filters/FilterBar/utils'
import { Paper } from '~/components/Global/Blocks/Paper/Paper'
import { QuickSortControl } from '~/components/Sort/QuickSortControl'
import { InvoiceList } from '~/domains/finance/components/InvoiceList'
import { buildInvoiceListApiParams } from '~/domains/finance/queries/invoiceListParams'
import {
  contentWrapper,
  contentWrapperWide
} from '~/pages/styles/Dashboard.css'
import { quickControlsBar } from '~/pages/styles/Orders.css'
import useDocumentTitle from '~/utils/documentTitle'
import { urlNumber } from '~/utils/zod'
import { Header } from './-components/Header'
import { useInvoiceFilterDefinitions } from './-components/invoiceFilterDefinitions'

const searchSchema = z.object({
  sort: z.string().optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  companyId: urlNumber,
  orderId: urlNumber,
  status: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  label: z.string().optional(),
  externalRef: z.string().optional(),
  id: urlNumber,
  userIds: z.string().optional(),
  dueDateFrom: z.string().optional(),
  dueDateTo: z.string().optional(),
  paymentDateFrom: z.string().optional(),
  paymentDateTo: z.string().optional()
})

const Finance = (): ReactElement => {
  const navigate = useNavigate({ from: '/finance/' })
  const {
    sort = 'issuedAt',
    sortDirection = 'desc',
    ...panelFilters
  } = useSearch({
    from: '/_dashboard/finance/'
  })

  const filterDefinitions = useInvoiceFilterDefinitions()
  const resolvedFilters = resolveFilters(
    { sort, sortDirection, ...panelFilters },
    filterDefinitions
  )

  useDocumentTitle('Facturation')

  const setFilters = useCallback(
    (filters: FiltersState, areDefault = false) => {
      const clean = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== '')
      )
      void navigate({
        search: {
          sort,
          sortDirection,
          ...clean
        },
        replace: areDefault
      })
    },
    [navigate, sort, sortDirection]
  )

  const setSortAndDirection = ({
    newSort = sort,
    newSortDirection = sortDirection
  }: {
    newSort?: string
    newSortDirection?: 'asc' | 'desc'
  }) => {
    void navigate({
      search: {
        sort: newSort,
        sortDirection: newSortDirection,
        ...panelFilters
      }
    })
  }

  const resetFilters = () => {
    void navigate({
      search: { sort, sortDirection }
    })
  }

  return (
    <div className={clsx(contentWrapper, contentWrapperWide)}>
      <Header />
      <Paper>
        <div className={quickControlsBar}>
          <FilterBar
            definitions={filterDefinitions}
            filters={toFiltersState(panelFilters)}
            onChange={setFilters}
            savedSearchesKey="search.saved_invoices"
          />
          <QuickSortControl
            sort={sort}
            sortDirection={sortDirection}
            options={[
              { label: "Date d'émission", value: 'issuedAt' },
              { label: 'Statut', value: 'status' }
            ]}
            onChange={({ sort: nextSort, sortDirection: nextSortDirection }) =>
              setSortAndDirection({
                newSort: nextSort,
                newSortDirection: nextSortDirection
              })
            }
          />
          <ExportListButton
            resourceKey="invoices"
            filename="factures.xlsx"
            params={buildInvoiceListApiParams(resolvedFilters)}
          />
        </div>
        <InvoiceList
          filters={resolvedFilters}
          onResetFilters={resetFilters}
          hideInlineFilters
        />
      </Paper>
    </div>
  )
}

export const Route = createFileRoute('/_dashboard/finance/')({
  validateSearch: searchSchema,
  component: Finance
})
